import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Meta } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { PinCardComponent } from '@pitchin/ui';

/**
 * P2P-29 (F0.4) — placeholder page for the Note product line. The FIRST new P2P screen; it sets the
 * precedent for lanes B–G (standalone component, built on `@pitchin/ui`, no new NgModule).
 *
 * The page is deliberately minimal — real listing behaviour (F2.1), the one-pager (F2.2) and the
 * landing/disclosure pages (F2.7) are out of scope. It exists so the `/note` routes resolve and render
 * something rather than 404-ing.
 *
 * SEO gating is driven by the route's `data.noindex` flag, which the router set by picking the flag-off
 * fallback route (see app.routing / notesEnabledOnGuard):
 *  - flag OFF → `data.noindex === true`. On PRODUCTION the app installs no site-wide robots tag, so this
 *    page adds its OWN `<meta name="robots" content="noindex">` — a deep link resolves but stays out of
 *    the index (Dry Run A can run a real cycle without exposing Notes). Nothing links to it, so it stays
 *    unreachable by browsing.
 *  - flag ON → the page is indexable; it adds nothing.
 *
 * It NEVER mutates the shared robots tag that `AppComponent.setHeaderTag()` installs app-wide (that tag
 * noindexes every page OUTSIDE production). This component only adds its own tag when none already
 * exists, and removes ONLY that self-created tag on destroy — so route-level SEO never strips
 * environment-level SEO.
 */
@Component({
  selector: 'app-note-placeholder',
  standalone: true,
  imports: [CommonModule, TranslateModule, PinCardComponent],
  template: `
    <section class="tw-mx-auto tw-max-w-3xl tw-px-4 tw-py-16">
      <pin-card variant="default" padding="lg">
        <h1 pin-card-header class="tw-text-2xl tw-font-semibold">
          {{ 'menu.noteTitle' | translate }}
        </h1>
        <p class="tw-mt-2 tw-text-base tw-text-gray-600">
          {{ 'menu.noteDescription' | translate }}
        </p>
      </pin-card>
    </section>
  `,
})
export class NotePlaceholderComponent implements OnInit, OnDestroy {
  /** True only when THIS component added the robots tag, so ngOnDestroy removes only its own. */
  private robotsTagOwned = false;

  constructor(
    private route: ActivatedRoute,
    private meta: Meta,
  ) {}

  ngOnInit(): void {
    // Add a route-level noindex ONLY for the flag-off route, and ONLY when no robots tag already exists
    // (i.e. on production, where the app installs no site-wide robots tag). On non-prod the shared
    // app-owned tag already noindexes everything, so we leave it untouched — and the flag-on route never
    // strips it.
    if (this.isNoindex() && !this.meta.getTag("name='robots'")) {
      this.meta.addTag({ name: 'robots', content: 'noindex' });
      this.robotsTagOwned = true;
    }
  }

  ngOnDestroy(): void {
    // Remove ONLY the tag we created — never the shared app-owned robots directive.
    if (this.robotsTagOwned) {
      this.meta.removeTag("name='robots'");
      this.robotsTagOwned = false;
    }
  }

  private isNoindex(): boolean {
    return this.route.snapshot.data['noindex'] === true;
  }
}
